import { useMutation, useQuery } from '@tanstack/react-query';
import { Contact, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api, type ContactView, type ProjectContactView } from '../../lib/client.ts';
import { Badge } from '../ui/badge.tsx';
import { Button } from '../ui/button.tsx';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.tsx';
import { EmptyState } from '../ui/empty-state.tsx';
import { Input } from '../ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';

const NEW = '__new';

/**
 * The Contacts tab (RELAY-952): the project's contacts as line items — name, role, primary. Add
 * an existing contact or a new one in the same form; making one primary demotes the other. Edit
 * opens the person's details and their role on this project in one dialog.
 */
export function ContactsTab({
  projectId,
  companyId,
  contacts,
  canWrite,
  onChanged,
}: {
  projectId: string;
  companyId: string;
  contacts: ProjectContactView[];
  canWrite: boolean;
  onChanged: () => Promise<void>;
}) {
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ProjectContactView | null>(null);
  const fail = (err: unknown) => setError(describeError(err));
  const done = async () => {
    setError(null);
    await onChanged();
  };
  const primary = useMutation({
    mutationFn: (contactId: string) =>
      api.projects.patchContact(projectId, contactId, { isPrimary: true }),
    onSuccess: done,
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: (contactId: string) => api.projects.removeContact(projectId, contactId),
    onSuccess: done,
    onError: fail,
  });
  return (
    <div className="flex flex-col gap-5" data-testid="contacts-tab">
      {contacts.length === 0 && (
        <EmptyState
          icon={Contact}
          title="No contacts on this project yet."
          description="Add the people at the client you work with on it."
        />
      )}
      {contacts.length > 0 && (
        <div className="bg-card border-border rounded-card shadow-card overflow-x-auto border">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead className="text-muted-foreground bg-muted text-xs">
              <tr>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Role</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Phone</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr
                  key={c.contactId}
                  className="border-border hover:bg-surface-hover/60 border-t transition-colors"
                  data-testid="contact-row"
                >
                  <td className="px-4 py-3 font-medium">
                    {c.contact.name}{' '}
                    {c.isPrimary && (
                      <Badge variant="brand" className="ml-1" data-testid="primary-badge">
                        Primary
                      </Badge>
                    )}
                  </td>
                  <td className="text-muted-foreground px-4 py-3">{c.role ?? '—'}</td>
                  <td className="px-4 py-3">{c.contact.email ?? '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{c.contact.phone ?? '—'}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {canWrite && (
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Edit ${c.contact.name}`}
                        onClick={() => setEditing(c)}
                      >
                        <Pencil />
                      </Button>
                    )}
                    {canWrite && !c.isPrimary && (
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Make ${c.contact.name} primary`}
                        onClick={() => primary.mutate(c.contactId)}
                      >
                        <Star />
                      </Button>
                    )}
                    {canWrite && (
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Remove ${c.contact.name}`}
                        onClick={() => remove.mutate(c.contactId)}
                      >
                        <Trash2 />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {error && (
        <p className="text-danger text-sm" role="alert">
          {error}
        </p>
      )}
      {canWrite && (
        <AddContactForm
          projectId={projectId}
          companyId={companyId}
          onAdded={done}
          onError={fail}
          already={new Set(contacts.map((c) => c.contactId))}
        />
      )}
      {editing && (
        <EditContactDialog
          projectId={projectId}
          link={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await done();
          }}
        />
      )}
    </div>
  );
}

/** The contact's own fields the dialog edits, in the order it shows them. */
const PERSON_FIELDS = [
  ['firstName', 'First name'],
  ['lastName', 'Last name'],
  ['jobTitle', 'Job title'],
  ['email', 'Email'],
  ['phone', 'Phone'],
] as const satisfies ReadonlyArray<readonly [keyof ContactView, string]>;
type PersonField = (typeof PERSON_FIELDS)[number][0];

/** Blank is no value, as the API stores it. */
const valueOf = (text: string) => text.trim() || null;

/**
 * Edit a contact from the project (the full record is read first: the line item carries only a
 * display name). The person's fields are the CONTACT (`PATCH /contacts/{id}`), so a change shows
 * on every project they are on; the role is this project's line item (`PATCH
 * /projects/{id}/contacts/{contactId}`). Each is sent only when something in it changed.
 */
function EditContactDialog({
  projectId,
  link,
  onClose,
  onSaved,
}: {
  projectId: string;
  link: ProjectContactView;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const contact = useQuery({
    queryKey: ['contacts', link.contactId],
    queryFn: () => api.contacts.get(link.contactId),
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {link.contact.name}</DialogTitle>
        </DialogHeader>
        {contact.data ? (
          <EditContactForm
            projectId={projectId}
            link={link}
            contact={contact.data}
            onClose={onClose}
            onSaved={onSaved}
          />
        ) : contact.error ? (
          <p className="text-danger text-sm" role="alert">
            {describeError(contact.error)}
          </p>
        ) : (
          <p className="text-muted-foreground text-sm">Loading…</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditContactForm({
  projectId,
  link,
  contact,
  onClose,
  onSaved,
}: {
  projectId: string;
  link: ProjectContactView;
  contact: ContactView;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [values, setValues] = useState<Record<PersonField, string>>(
    () =>
      Object.fromEntries(PERSON_FIELDS.map(([k]) => [k, contact[k] ?? ''])) as Record<
        PersonField,
        string
      >,
  );
  const [role, setRole] = useState(link.role ?? '');
  const [error, setError] = useState<string | null>(null);
  const changed = Object.fromEntries(
    PERSON_FIELDS.filter(([k]) => valueOf(values[k]) !== contact[k]).map(([k]) => [
      k,
      valueOf(values[k]),
    ]),
  );
  const roleChanged = valueOf(role) !== link.role;
  // RecruitCRM refuses a contact with no name, so both names are required, an old nameless
  // contact included: it cannot be saved until someone gives it one.
  const named = !!(valueOf(values.firstName) && valueOf(values.lastName));
  const save = useMutation({
    mutationFn: async () => {
      if (Object.keys(changed).length > 0) await api.contacts.patch(contact.id, changed);
      if (roleChanged)
        await api.projects.patchContact(projectId, contact.id, { role: valueOf(role) });
    },
    onSuccess: onSaved,
    onError: (err) => setError(describeError(err)),
  });
  return (
    <>
      <form
        id="edit-contact"
        className="grid grid-cols-1 gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {PERSON_FIELDS.map(([k, label]) => (
          <label key={k} className="flex flex-col gap-1.5 text-sm font-medium">
            <span>
              {label}
              {(k === 'firstName' || k === 'lastName') && (
                <span className="text-danger" aria-hidden="true">
                  {' '}
                  *
                </span>
              )}
            </span>
            <Input
              aria-label={label}
              required={k === 'firstName' || k === 'lastName'}
              type={k === 'email' ? 'email' : k === 'phone' ? 'tel' : 'text'}
              value={values[k]}
              onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))}
            />
          </label>
        ))}
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          Role on this project
          <Input
            aria-label="Role on this project"
            placeholder="e.g. Hiring manager"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          />
        </label>
        <p className="text-muted-foreground text-xs sm:col-span-2">
          Name, title, email and phone change on every project this contact is on; the role is this
          project&rsquo;s only.
        </p>
        {!named && (
          <p className="text-danger text-sm sm:col-span-2" role="alert">
            First name and last name are required.
          </p>
        )}
        {error && (
          <p className="text-danger text-sm sm:col-span-2" role="alert">
            {error}
          </p>
        )}
      </form>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="edit-contact"
          disabled={!named || save.isPending || (Object.keys(changed).length === 0 && !roleChanged)}
        >
          Save
        </Button>
      </DialogFooter>
    </>
  );
}

function AddContactForm({
  projectId,
  companyId,
  already,
  onAdded,
  onError,
}: {
  projectId: string;
  companyId: string;
  already: ReadonlySet<string>;
  onAdded: () => Promise<void>;
  onError: (err: unknown) => void;
}) {
  const existing = useQuery({ queryKey: ['contacts'], queryFn: () => api.contacts.list() });
  const [pick, setPick] = useState<string>(NEW);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const add = useMutation({
    mutationFn: async () => {
      let contactId = pick;
      if (pick === NEW) {
        const created = await api.contacts.create({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          ...(email.trim() ? { email: email.trim() } : {}),
          companyId,
        });
        contactId = created.id;
      }
      return api.projects.addContact(projectId, {
        contactId,
        role: role.trim() || null,
        isPrimary,
      });
    },
    onSuccess: async () => {
      setPick(NEW);
      setFirstName('');
      setLastName('');
      setEmail('');
      setRole('');
      setIsPrimary(false);
      await onAdded();
    },
    onError,
  });
  const choices = (existing.data?.items ?? []).filter((c) => !already.has(c.id));
  const ready = pick !== NEW || (!!firstName.trim() && !!lastName.trim());
  return (
    <form
      className="border-border bg-card/50 flex flex-wrap items-center gap-3 rounded-card border border-dashed p-4"
      data-testid="add-contact"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) add.mutate();
      }}
    >
      <Select value={pick} onValueChange={setPick}>
        <SelectTrigger className="w-56" aria-label="Contact">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NEW}>New contact…</SelectItem>
          {choices.map((c) => (
            <SelectItem key={c.id} value={c.id}>
              {[c.firstName, c.lastName].filter(Boolean).join(' ') || c.email}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {pick === NEW && (
        <>
          <Input
            aria-label="Contact first name"
            placeholder="First name *"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="w-40"
          />
          <Input
            aria-label="Contact last name"
            placeholder="Last name *"
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="w-40"
          />
          <Input
            aria-label="Contact email"
            placeholder="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-52"
          />
        </>
      )}
      <Input
        aria-label="Role on project"
        placeholder="Role (e.g. Hiring manager)"
        value={role}
        onChange={(e) => setRole(e.target.value)}
        className="w-52"
      />
      <label className="text-muted-foreground flex items-center gap-1.5 text-xs">
        <input
          type="checkbox"
          checked={isPrimary}
          onChange={(e) => setIsPrimary(e.target.checked)}
          aria-label="Primary contact"
        />
        Primary
      </label>
      <Button size="sm" type="submit" disabled={!ready || add.isPending}>
        <Plus /> Add contact
      </Button>
    </form>
  );
}
